import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";
import Reveal from "@/components/Reveal";
import { IMAGES } from "@/data/content";

const IMAGE_ALTS = {
  ceramic: "Satin grey Mercedes-AMG G63 in the studio",
  ppf: "Grey Ferrari in the brick-walled studio",
  before: "Before — dust and water-spotted Audi S3 in the studio",
  after: "After — the same Audi S3, gloss-black and spotless",
  inspect: "Detailer inspecting a black BMW M2 with a handheld light",
  correct: "Dual-action polisher correcting gloss black paint",
  protect: "Paint protection film being squeegeed onto a black BMW",
  deliver: "Finished black BMW M4 front-on under studio lights",
  wheel: "Audi RS wheel — black rim with red brake caliper",
  interior: "Mercedes-AMG interior — carbon and perforated leather steering wheel over red leather",
  lambo: "Lamborghini interior — embroidered headrest and yellow-stitched black leather",
  corvette: "Black Corvette parked in the studio garage",
  team: "Luxpress detailer steam-cleaning a leather interior",
  studio: "Machine polishing a black car in the Luxpress studio",
};

const STUBS = {
  gallery: {
    kicker: "Gallery",
    title: ["The work,", "on the wall."],
    meta: "Gallery — Luxpress Detailing",
    desc: "Before and after transformations from the Luxpress Detailing studio in South Melbourne. Full gallery coming soon.",
    body: "Every frame shot in the studio, under the same lights we inspect under. Correction, coating, film and interior work — as it actually looks when it leaves the bay.",
    images: ["ceramic", "ppf", "before", "after", "inspect", "correct", "protect", "deliver", "wheel", "interior", "lambo", "team", "studio"],
  },
  about: {
    kicker: "About",
    title: ["The studio,", "the standard."],
    meta: "About — Luxpress Detailing, South Melbourne",
    desc: "The people and the standard behind Luxpress Detailing, 476 City Road, South Melbourne.",
    body: "The full story of the studio — the people, the years, the products we trust — is being written. What won't change: pH-neutral chemicals, premium products only, and a finish we inspect under our own lights before you see it.",
    images: ["team", "studio"],
  },
  journal: {
    kicker: "Journal",
    title: ["Notes from", "the bay."],
    meta: "Journal — Luxpress Detailing",
    desc: "Detailing notes from Luxpress Detailing: why professional detailing matters, paint care, protection and finish.",
    body: "The journal launches with our first piece and grows from there — paint care, protection choices, and what actually happens to a car in the bay.",
    article: "Why Professional Detailing Matters: More Than Just a Clean Car",
    images: [],
  },
};

const Stub = ({ page }) => {
  const s = STUBS[page];
  usePageMeta(s.meta, s.desc);
  return (
    <div className="mx-auto max-w-7xl px-5 pb-24 pt-36 sm:px-8 sm:pt-44" data-testid={`stub-${page}`}>
      <Reveal>
        <p className="kicker">{s.kicker}</p>
        <h1 className="mt-4 font-display text-4xl font-black uppercase leading-[0.95] tracking-tighter sm:text-6xl">
          {s.title[0]}<br /><span className="text-mint">{s.title[1]}</span>
        </h1>
        <p className="mt-6 max-w-xl text-base leading-relaxed text-chrome">{s.body}</p>
      </Reveal>

      {s.article && (
        <Reveal delay={0.1} className="mt-14">
          <div className="border border-dashed border-teal-line bg-panel/50 p-8 sm:p-10">
            <img
              src={IMAGES.corvette}
              alt="Black Corvette under studio lighting"
              loading="lazy"
              className="mb-8 aspect-[16/8] w-full border border-teal-line object-cover"
            />
            <p className="spec-label">First article — placeholder</p>
            <h2 className="mt-3 max-w-xl font-display text-2xl font-bold uppercase tracking-tight">{s.article}</h2>
            <p className="mt-3 max-w-lg text-sm leading-relaxed text-chrome">
              The difference between professional detailing and a basic wash — the existing article moves here,
              in a template built to scale past one post.
            </p>
          </div>
        </Reveal>
      )}

      {s.images.length > 0 && (
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {s.images.map((key, i) => (
            <Reveal key={key} delay={i * 0.07}>
              <img
                src={IMAGES[key]}
                alt={IMAGE_ALTS[key]}
                loading="lazy"
                className={`aspect-[4/3] w-full border border-teal-line object-cover ${key === "team" ? "object-top" : ""}`}
              />
            </Reveal>
          ))}
          {page === "about" && (
            <Reveal delay={0.15}>
              <img
                src="/assets/ppf-detail.jpg"
                alt="Paint protection film being squeegeed onto a black BMW side skirt"
                loading="lazy"
                className="aspect-[4/3] w-full border border-teal-line object-cover"
              />
            </Reveal>
          )}
        </div>
      )}

      <Reveal className="mt-16">
        <Link to="/contact" data-testid={`stub-${page}-cta`} className="btn-primary">
          Request a quote <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Link>
      </Reveal>
    </div>
  );
};

export default Stub;
